import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Ricerca } from './anagrafiche.js';
import { chiama } from './cliente.js';

export interface RigaArticolo {
  id: number;
  sku: string;
  description: string;
  bcNumber: string | null;
  line: string | null;
  product: string | null;
  typology: string | null;
  packaging: string | null;
  format: string | null;
  variant: string | null;
  casePack: string | null;
  unit: string | null;
  gsCode: string | null;
  packFormatGr: string | null;
  archived: boolean;
}

export interface Articolo {
  id: number;
  sku: string;
  description: string;
  bcNumber: string | null;
  alias: string | null;
  crossRef: string | null;

  codeG12: string | null;
  /** I codici di questo articolo che anche un altro articolo ha. */
  gsDuplicates?: { code: string; articleId: number; sku: string }[];
  codeG13: string | null;
  codeG14A: string | null;
  codeG14B: string | null;
  codeG14APackage: string | null;
  codeG14BPackage: string | null;

  lineId: number | null;
  line: string | null;
  lineBcPrefix: string | null;
  productId: number | null;
  product: string | null;
  typologyId: number | null;
  typology: string | null;
  packagingId: number | null;
  packaging: string | null;
  formatId: number | null;
  format: string | null;
  variantId: number | null;
  variant: string | null;
  variantGeneratesGs: boolean | null;

  casePackId: number | null;
  casePack: string | null;
  casePackGsNumber: string | null;
  unitOfMeasurementId: number | null;
  unit: string | null;
  unitAbbreviation: string | null;

  grossFormatGr: string | null;
  packFormatGr: string | null;
  packFormatOz: string | null;
  packsPerPallet: string | null;
  platformsPerContainer: string | null;

  casePackDimensions: string | null;
  casePackGrossWeight: string | null;
  casePackCubed: string | null;
  casePacksPerPallet: string | null;
  palletConfiguration: string | null;
  palletGrossWeight: string | null;
  unitDimensions: string | null;
  unitSize: string | null;
  unitWeight: string | null;

  notes: string | null;
  archived: boolean;
  companies: { id: number; code: string; name: string }[];
  /** Quanto è stato ordinato di questo articolo nell'azienda scelta. */
  usage: { orders: number; quantity: number; lastOrderDate: string | null } | null;
  /**
   * Lo SKU che verrebbe fuori dagli attributi. È un suggerimento e basta:
   * nel v2 la generazione automatica è stata spenta da quando gli articoli
   * arrivano da Business Central, e rimetterla in moto riscriverebbe 558 SKU
   * giusti.
   */
  skuFromAttributes: string | null;
  /** Compare dopo un salvataggio che ha toccato campi di Business Central. */
  warning?: string;
}

export interface CodiciGs1 {
  codeG12: string;
  codeG13: string;
  codeG14A: string;
  codeG14B: string;
  codeG14APackage: string;
  codeG14BPackage: string;
}

/** Cosa risponde l'anteprima dei codici GS1. */
export interface AnteprimaGs1 {
  ok: boolean;
  error?: string;
  message?: string;
  codes?: CodiciGs1;
  duplicati?: { code: string; articleId: number; sku: string }[];
}

interface Pagina {
  rows: RigaArticolo[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

export const RICERCA_ARTICOLI: Ricerca = {
  page: 1,
  size: 50,
  sort: 'description',
  dir: 'asc',
  q: '',
  archived: false,
  filters: {},
};

function queryString(r: Ricerca, extra: Record<string, string> = {}) {
  return new URLSearchParams({
    page: String(r.page),
    size: String(r.size),
    sort: r.sort,
    dir: r.dir,
    ...(r.q ? { q: r.q } : {}),
    ...(r.archived ? { archived: 'true' } : {}),
    ...Object.fromEntries(
      Object.entries(r.filters ?? {})
        .filter(([, v]) => v !== undefined && v !== '')
        .map(([k, v]) => [k, String(v)]),
    ),
    ...extra,
  }).toString();
}

/** Con `attiva` a falso non parte: per le ricerche che aspettano due lettere. */
export function useArticoli(ricerca: Ricerca, attiva = true) {
  return useQuery({
    queryKey: ['articoli', ricerca],
    queryFn: () => chiama<Pagina>(`/api/items?${queryString(ricerca)}`),
    placeholderData: (precedente) => precedente,
    enabled: attiva,
  });
}

export function useArticolo(id: number) {
  return useQuery({
    queryKey: ['articolo', id],
    queryFn: () => chiama<Articolo>(`/api/items/${id}`),
    enabled: Number.isInteger(id) && id > 0,
  });
}

/**
 * Salva quello che è cambiato sulla scheda.
 *
 * Si manda solo ciò che si è toccato, come sulle anagrafiche: due persone
 * sulla stessa scheda non si sovrascrivono i campi che non hanno guardato.
 */
export function useSalvaArticolo(id: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (dati: Record<string, unknown>) =>
      chiama<Articolo>(`/api/items/${id}`, { metodo: 'PATCH', corpo: dati }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['articolo', id] });
      // l'anteprima dei codici dipende da case pack e variante: cambiati quelli,
      // restava quella di prima
      void client.invalidateQueries({ queryKey: ['gs1', id] });
      void client.invalidateQueries({ queryKey: ['articoli'] });
    },
  });
}

/** Che codici uscirebbero, senza scriverli. */
export function useAnteprimaGs1(id: number, attiva: boolean) {
  return useQuery({
    queryKey: ['gs1', id],
    queryFn: () => chiama<AnteprimaGs1>(`/api/items/${id}/gs1`),
    enabled: attiva && Number.isInteger(id) && id > 0,
  });
}

/**
 * Genera i codici GS1 e li scrive.
 *
 * `only14` rifà i soli quattro GTIN-14: è il secondo pulsante del v2, e serve
 * quando GTIN-12 e GTIN-13 sono codici comprati che non si toccano.
 */
export function useGeneraGs1(id: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ only14, force }: { only14?: boolean; force?: boolean } = {}) =>
      chiama<Articolo & { written: string[] }>(`/api/items/${id}/gs1`, {
        metodo: 'POST',
        corpo: { ...(only14 ? { only14: true } : {}), ...(force ? { force: true } : {}) },
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['articolo', id] });
      void client.invalidateQueries({ queryKey: ['gs1', id] });
      void client.invalidateQueries({ queryKey: ['articoli'] });
    },
  });
}

export function useDefinizioneFiltriArticoli() {
  return useQuery({
    queryKey: ['filtri-articoli'],
    queryFn: () =>
      chiama<{
        filters: {
          key: string;
          label: string;
          type: 'text' | 'list' | 'yesNo' | 'choice' | 'date';
          options?: string;
          multi?: boolean;
        }[];
        options: Record<string, { id: string | number; label: string }[]>;
      }>('/api/items/filters'),
    staleTime: 10 * 60_000,
  });
}

export function esportaArticoli(ricerca: Ricerca, formato: 'xlsx' | 'csv'): void {
  window.location.href = `/api/items/export?${queryString(ricerca, { format: formato })}`;
}
